/**
 * STEP 8: SCOPE DESCRIPTION
 * 
 * Write a narrative description of the project scope that will feed the AI estimate generator.
 * This is the "story" of what we're doing.
 * 
 * Theme: Spec Ops
 * Based on Document 5 specification
 */

import React, { useState, useMemo } from 'react';
import StepContainer from './StepContainer';
import { FileText, Wand2, Copy, Check, AlertCircle, MapPin, Package, Sparkles } from 'lucide-react';

// Labels for fixture keys (simplified version)
const FIXTURE_LABELS = {
  vanity: 'Vanity',
  toilet: 'Toilet',
  tub: 'Tub',
  shower: 'Shower',
  sink: 'Sink',
  faucet: 'Faucet',
  lighting: 'Lighting',
  exhaust_fan: 'Exhaust Fan',
  mirror: 'Mirror',
  flooring: 'Flooring',
  wall_tile: 'Wall Tile',
  countertop: 'Countertop',
  cabinets: 'Cabinets',
  range: 'Range',
  refrigerator: 'Refrigerator',
  dishwasher: 'Dishwasher',
  microwave: 'Microwave',
  hood: 'Hood',
  interior_doors: 'Interior Doors',
  windows: 'Windows',
  trim: 'Trim',
  paint: 'Paint',
  hvac: 'HVAC',
  water_heater: 'Water Heater',
  washer_hookup: 'Washer Hookup',
  dryer_hookup: 'Dryer Hookup',
};

const Step8ScopeDescription = ({ 
  data = {},
  areas = [],
  productSelections = {},
  clientDiscovery = {},
  onUpdate,
  onSave,
  onPrev,
  onNext,
  saving = false,
  isComplete = false,
  validationErrors = [],
  settings = {},
}) => {
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const updateField = (key, value) => {
    if (isComplete) return;
    onUpdate?.('scope_description', { ...data, [key]: value });
  };

  const minChars = settings?.step8_settings?.min_characters || 100;
  const charCount = (data.narrative || '').length;
  const isMinMet = charCount >= minChars;

  // Get areas that have work (replacements/additions)
  const areasWithWork = useMemo(() => {
    return areas.filter(area => {
      if (!area.fixtures) return false;
      return Object.values(area.fixtures).some(
        f => f.condition === 'replace' || f.condition === 'add_new'
      );
    }).map(area => {
      // Get replacements for this area
      const replacements = Object.entries(area.fixtures || {})
        .filter(([_, f]) => f.condition === 'replace' || f.condition === 'add_new')
        .map(([key, _]) => FIXTURE_LABELS[key] || key);
      
      // Get product count for this area
      const productCount = (productSelections?.selections || [])
        .filter(s => s.area_id === area.area_id).length;
      
      return {
        ...area,
        replacements,
        productCount,
      };
    });
  }, [areas, productSelections]);

  // Toggle area inclusion
  const toggleAreaInclusion = (areaId) => {
    if (isComplete) return;
    
    const currentIncluded = data.areas_included || areasWithWork.map(a => a.area_id);
    const isIncluded = currentIncluded.includes(areaId);
    
    const newIncluded = isIncluded
      ? currentIncluded.filter(id => id !== areaId)
      : [...currentIncluded, areaId];
    
    updateField('areas_included', newIncluded);
  };

  // Check if area is included
  const isAreaIncluded = (areaId) => {
    // If areas_included not set, default to all areas
    if (!data.areas_included) return true;
    return data.areas_included.includes(areaId);
  };

  // Generate AI draft from data
  const generateFromData = async () => {
    if (isComplete) return;
    setGenerating(true);
    
    try {
      const includedAreas = areasWithWork.filter(a => isAreaIncluded(a.area_id));
      
      // Call the AI scope narrative API
      const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${API_BASE}/api/ai/generate-scope-narrative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          areas: includedAreas.map(area => ({
            label: area.label,
            type: area.type,
            fixtures: area.fixtures || {},
            dimensions: area.dimensions || {},
          })),
          products: productSelections?.selections || [],
          client_context: {
            problem: clientDiscovery?.problem_description || '',
            success_definition: clientDiscovery?.success_definition || '',
            timeline: clientDiscovery?.timeline_desired_start || '',
            budget: clientDiscovery?.budget_range || '',
            staying_during_work: clientDiscovery?.staying_during_work || '',
          },
          risks: [], // Will be filled from risk data if available
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate scope narrative');
      }

      const result = await response.json();
      
      if (result.success && result.narrative) {
        updateField('narrative', result.narrative);
        updateField('ai_generated', true);
        updateField('areas_included', includedAreas.map(a => a.area_id));
      } else {
        // Fallback to local generation if API fails
        await generateLocalDraft(includedAreas);
      }
    } catch (error) {
      console.error('AI generation error:', error);
      // Fallback to local generation
      const includedAreas = areasWithWork.filter(a => isAreaIncluded(a.area_id));
      await generateLocalDraft(includedAreas);
    } finally {
      setGenerating(false);
    }
  };

  // Local fallback draft generation
  const generateLocalDraft = async (includedAreas) => {
    // Build narrative from structured data
    let narrative = `PROJECT SCOPE DESCRIPTION\n\n`;
    
    // Add problem/success context
    if (clientDiscovery?.problem_description) {
      narrative += `OBJECTIVE:\n${clientDiscovery.problem_description}\n\n`;
    }
    
    if (clientDiscovery?.success_definition) {
      narrative += `SUCCESS CRITERIA:\n${clientDiscovery.success_definition}\n\n`;
    }
    
    narrative += `SCOPE OF WORK:\n\n`;
    
    // Add each area
    includedAreas.forEach(area => {
      const sqft = area.dimensions?.floor_sf ? ` (${Math.round(area.dimensions.floor_sf)} SF)` : '';
      narrative += `${area.label.toUpperCase()}${sqft}\n`;
      
      if (area.replacements.length > 0) {
        narrative += `- Remove and replace: ${area.replacements.join(', ')}\n`;
      }
      
      if (area.productCount > 0) {
        narrative += `- ${area.productCount} product selections specified\n`;
      }
      
      narrative += `- [Add specific work details for this area]\n\n`;
    });
    
    narrative += `GENERAL REQUIREMENTS:\n`;
    narrative += `- Protect all adjacent surfaces and flooring during work\n`;
    narrative += `- Daily cleanup and debris removal\n`;
    narrative += `- Final cleaning upon completion\n`;
    narrative += `- Coordinate with homeowner on work schedule\n\n`;
    
    if (clientDiscovery?.staying_during_work === 'yes') {
      narrative += `NOTE: Homeowner will remain in residence during construction.\n\n`;
    }
    
    narrative += `EXCLUSIONS:\n`;
    narrative += `- [List any items NOT included in this scope]\n`;

    updateField('narrative', narrative);
    updateField('ai_generated', false); // Mark as locally generated
    updateField('areas_included', includedAreas.map(a => a.area_id));
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(data.narrative || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <StepContainer
      step={8}
      onPrev={onPrev}
      onNext={onNext}
      onSave={onSave}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
    >
      <div className="space-y-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-slate-400 text-sm">
              Describe the project scope in your own words. This narrative helps generate
              an accurate estimate with proper organization.
            </p>
            <p className="text-xs text-slate-500 mt-1">
              Minimum {minChars} characters required
            </p>
          </div>
          
          {!isComplete && settings?.step8_settings?.ai_assist_enabled !== false && (
            <button
              onClick={generateFromData}
              disabled={generating || areasWithWork.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-500 
                text-white font-medium transition-colors disabled:opacity-50 flex-shrink-0"
              data-testid="ai-assist-button"
            >
              <Sparkles className="w-4 h-4" />
              {generating ? 'Generating...' : 'AI Assist'}
            </button>
          )}
        </div>

        {/* Areas Included */}
        <div className="p-4 bg-slate-800/50 border border-slate-700 rounded space-y-3">
          <h3 className="text-sm font-semibold text-slate-400 flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            Areas Included
          </h3>
          
          {areasWithWork.length > 0 ? (
            <div className="space-y-2">
              {areasWithWork.map(area => (
                <label 
                  key={area.area_id}
                  className={`
                    flex items-start gap-3 p-3 border rounded cursor-pointer transition-all
                    ${isAreaIncluded(area.area_id)
                      ? 'border-emerald-500/40 bg-emerald-500/10'
                      : 'border-slate-700 bg-slate-800/30 opacity-60'
                    }
                    ${isComplete ? 'cursor-default' : 'hover:border-slate-600'}
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isAreaIncluded(area.area_id)}
                    onChange={() => toggleAreaInclusion(area.area_id)}
                    disabled={isComplete}
                    className="mt-1 w-4 h-4 bg-slate-900 border-slate-600 rounded 
                      text-emerald-500 focus:ring-emerald-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-200">{area.label}</span>
                      {area.dimensions?.floor_sf && (
                        <span className="text-xs text-slate-500 ml-2">
                          {Math.round(area.dimensions.floor_sf)} SF
                        </span>
                      )}
                    </div>
                    {area.replacements.length > 0 && (
                      <p className="text-xs text-slate-400 mt-1">
                        Replace: {area.replacements.slice(0, 4).join(', ')}
                        {area.replacements.length > 4 && ` +${area.replacements.length - 4} more`}
                      </p>
                    )}
                    {area.productCount > 0 && (
                      <p className="text-xs text-emerald-400/80 mt-0.5 flex items-center gap-1">
                        <Package className="w-3 h-3" />
                        {area.productCount} product{area.productCount !== 1 ? 's' : ''} selected
                      </p>
                    )}
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <div className="p-4 text-center border border-dashed border-slate-700 rounded">
              <p className="text-slate-500 text-sm">
                No areas with work specified yet.
              </p>
              <p className="text-xs text-slate-600 mt-1">
                Go to Step 4 to add areas and mark fixtures for replacement.
              </p>
            </div>
          )}
        </div>

        {/* Main Narrative */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-300 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Project Narrative <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-3">
              {data.narrative && (
                <button
                  onClick={copyToClipboard}
                  className="p-1.5 text-slate-400 hover:text-slate-200 hover:bg-slate-700 rounded transition-colors"
                  title="Copy to clipboard"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                </button>
              )}
              <span className={`text-xs font-medium ${isMinMet ? 'text-emerald-400' : 'text-amber-400'}`}>
                {charCount} / {minChars} chars
              </span>
            </div>
          </div>
          
          <textarea
            value={data.narrative || ''}
            onChange={(e) => updateField('narrative', e.target.value)}
            disabled={isComplete}
            rows={16}
            className={`
              w-full px-4 py-3 bg-slate-950 border text-slate-100 rounded
              focus:outline-none disabled:opacity-50 resize-none font-mono text-sm leading-relaxed
              ${isMinMet ? 'border-slate-700 focus:border-emerald-500' : 'border-amber-500/50 focus:border-amber-500'}
            `}
            placeholder={`Describe the complete scope of work...

Include:
• What's being demolished/removed
• What's being installed/replaced
• Specific products or materials selected
• Any special requirements or considerations
• Work sequence if relevant
• Protection requirements
• Cleanup expectations

Be specific - this drives the estimate accuracy.`}
            data-testid="scope-narrative-input"
          />
          
          {data.ai_generated && (
            <p className="text-xs text-purple-400 flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              AI-generated draft - review and customize as needed
            </p>
          )}
          
          {!isMinMet && charCount > 0 && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <AlertCircle className="w-3 h-3" />
              {minChars - charCount} more characters needed
            </p>
          )}
        </div>

        {/* Special Instructions */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-300">
            Special Instructions
            <span className="text-slate-500 font-normal ml-2">(Additional notes for the estimate)</span>
          </label>
          <textarea
            value={data.special_instructions || ''}
            onChange={(e) => updateField('special_instructions', e.target.value)}
            disabled={isComplete}
            rows={4}
            className="w-full px-4 py-3 bg-slate-950 border border-slate-700 text-slate-100 rounded
              focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            placeholder="Include floor protection for hallway and stairs.
Add dumpster rental for demo debris.
Include final clean.
Coordinate with electrician for panel upgrade."
            data-testid="special-instructions-input"
          />
        </div>

        {/* Tips */}
        <div className="p-4 bg-slate-800/30 border border-slate-700 rounded">
          <h4 className="text-sm font-semibold text-slate-400 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Tips for a Great Scope Description
          </h4>
          <ul className="text-xs text-slate-500 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Be specific about quantities and sizes when known
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Reference the product selections made in Step 6
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Include any demolition or prep work required
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Note any coordination with other trades
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Mention protection requirements (floors, furniture, etc.)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-500 mt-0.5">✓</span>
              Include cleanup and debris removal expectations
            </li>
          </ul>
        </div>
      </div>
    </StepContainer>
  );
};

export default Step8ScopeDescription;
